import React, { useState } from "react";
import { useFormikContext, getIn } from "formik";
import { Link, navigate } from "gatsby";
import { BeatLoader } from "react-spinners";

import { PlanDetails } from "./PlanDetails";
import { ConfigurationsSummary } from "./ConfigurationsSummary";
import { PriceSummary } from "./PriceSummary";
import PrimaryButton from "../../inputs/buttons/Button";

import { getFooterNavLinks } from "../../../../utils/getFooterNavLinks";
import { useSelectedPlanContext } from "../../../../contexts/SelectedPlanContext";
import { usePageSettings } from "../../../../contexts/PageSettingsContext";
import { useCheckout } from "../../../../contexts/CheckoutPageContext";
import { useFetchAddonPrice } from "../../../../hooks/useFetchAddonPrice";
import { useFetchPrice } from "../../../../hooks/useFetchPlanPrice";
import { useFetchTotalPrice } from "../../../../hooks/useFetchTotalPrice";
import { buildOrderPayload } from "../../../../utils/buildOrderPayload"; // <-- move build logic here
import { motion } from "framer-motion";

// Tab field mapping
const tabFieldMap = {
	vps: {
		0: [
			"configuration.serverName",
			"configuration.os.id",
			"configuration.paymentCycle",
			"configuration.additionalIp.id",
		],
		1: ["configuration.paymentMethod.module", "configuration.paymentProfile.id"],
	},
	baremetal: {
		0: [
			"configuration.serverName",
			"configuration.os.id",
			"configuration.paymentCycle",
			"configuration.additionalIp.id",
		],
		1: ["configuration.paymentMethod.module", "configuration.paymentProfile.id"],
	},
	colocation: {
		0: ["configuration.paymentCycle"],
		1: ["configuration.paymentMethod.module", "configuration.paymentProfile.id"],
	},
	storage: {
		0: ["configuration.paymentCycle"],
		1: ["configuration.paymentMethod.module", "configuration.paymentProfile.id"],
	},
	bgp: {
		0: ["configuration.paymentCycle"],
		1: ["configuration.paymentMethod.module", "configuration.paymentProfile.id"],
	},
	ultraproxy: {
		0: ["configuration.paymentCycle"],
		1: ["configuration.paymentMethod.module", "configuration.paymentProfile.id"],
	},
	iptransit: {
		0: ["configuration.paymentCycle"],
		1: ["configuration.paymentMethod.module", "configuration.paymentProfile.id"],
	},
	webhosting: {
		0: ["configuration.paymentCycle"],
		1: ["configuration.paymentMethod.module", "configuration.paymentProfile.id"],
	},
};

const OrderSummary = ({ pageContext, currentTab, setCurrentTab, setOrderNumber, legalUrl }) => {
	// Local state for the success modal
	const [orderCreated, setOrderCreated] = useState(false);
	// const [orderNumber, setOrderNumber] = useState("");
	// Pull Formik data & helpers
	const { values, validateForm, setFieldValue, isSubmitting, errors } = useFormikContext();

	// Grab info from contexts
	const { selectedPlan } = useSelectedPlanContext(); // to build final payload
	const { selectedLocation, translations, currency } = usePageSettings(); // to build final payload
	const { orderCount } = useCheckout();

	const paymentCycle = values?.configuration?.paymentCycle;
	const serviceType = selectedPlan?.service; // e.g. "vps", "webhosting", etc.

	// Gathers selected addons into an array of IDs
	function gatherAddonIds(configuration) {
		const addonIds = [];
		if (configuration.os?.id) {
			addonIds.push(configuration.os.id);
		}
		if (configuration.additionalIp?.id) {
			addonIds.push(configuration.additionalIp.id);
		}
		if (configuration.bgp?.enabled && configuration.bgp.id) {
			addonIds.push(configuration.bgp.id);
		}
		// Add more fields for other services if needed
		return addonIds;
	}

	const addonIds = gatherAddonIds(values.configuration);

	// Pricing hooks (just reading data)
	const { data: planPrice, isLoading: planPriceLoading } = useFetchPrice(serviceType, paymentCycle);
	const { data: addonPrice, isLoading: addonPriceLoading } = useFetchAddonPrice({ addonIds, paymentCycle });
	const { data: totalPrice, isLoading: totalPriceLoading } = useFetchTotalPrice({ addonIds, paymentCycle });

	//const { about } = getFooterNavLinks(pageContext);
	const { about } = getFooterNavLinks(pageContext || {});
	const [isSubmittingT, setIsSubmittingT] = useState(false);

	// Which fields do we care about on this tab?
	const fieldsForCurrentTab = tabFieldMap[serviceType]?.[currentTab] || [];

	// Are there any errors for these fields?
	const hasTabErrors = fieldsForCurrentTab.some((path) => getIn(errors, path) !== undefined);

	// ----------------------------------------
	//  Handle final order creation here
	// ----------------------------------------
	const handleCreateOrder = async () => {
		try {
			// 1) Put orderCount in the form values
			setFieldValue("orderCount", orderCount);
			// 2) Validate entire form
			const allErrors = await validateForm();
			if (Object.keys(allErrors).length > 0) {
				// If there's any form error, do not proceed
				// console.warn("Form validation errors => cannot create order:", allErrors);
				return;
			}

			// 3) Build final payload
			const finalPayload = buildOrderPayload(
				{
					...values,
					service: selectedPlan?.service,
				},
				selectedPlan,
				currency,
				selectedLocation,
				orderCount
			);
			// console.log("Final Payload:", finalPayload);

			// 4) Do the fetch
			const response = await fetch(process.env.GATSBY_CREATE_ORDER_ENDPOINT, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(finalPayload),
			});
			const data = await response.json();

			//console.log("Order created:", data);

			// 5) Show the success modal
			setOrderCreated(true);
			setOrderNumber(data);
			setCurrentTab(2);
		} catch (err) {
			console.error("Error creating order:", err);
		}
	};

	// For partial tab flow:
	const handleButtonClick = async () => {
		if (isSubmittingT) return;
		setIsSubmittingT(true);

		if (currentTab === 0) {
			// PARTIAL VALIDATION ON TAB 0
			const allErrors = await validateForm();
			const fieldsForTab0 = tabFieldMap?.[serviceType]?.[0] || [];
			const hasTab0Errors = fieldsForTab0.some((path) => getIn(allErrors, path) !== undefined);

			if (hasTab0Errors) {
				// console.log("Tab 0 errors => do not proceed. Errors:", allErrors);
				return;
			}

			// Move to tab 1
			setCurrentTab(1);
			scrollToTopMobile();
		} else if (currentTab === 1) {
			// PARTIAL VALIDATION ON TAB 1
			const allErrors = await validateForm();
			const fieldsForTab1 = tabFieldMap?.[serviceType]?.[1] || [];
			const hasTab1Errors = fieldsForTab1.some((path) => getIn(allErrors, path) !== undefined);

			if (hasTab1Errors) {
				console.log("Tab 1 errors => do not proceed. Errors:", allErrors);
				return;
			} else {
				// If no tab1 errors => create the order

				await handleCreateOrder();
			}
		}
		setIsSubmittingT(false);
	};

	const scrollToTopMobile = () => {
		if (window.innerWidth <= 768) {
			setTimeout(() => {
				const targetElement = document.getElementById("scroll-target");
				if (targetElement) {
					targetElement.scrollIntoView({ behavior: "smooth" });
				} else {
					window.scrollTo({ top: 0, behavior: "smooth" });
				}
			}, 500);
		}
	};

	return (
		<div className="flex flex-col gap-7 px-4 md:px-0 items-end w-full">
			<p className="text-base font-bold text-neutral-50 w-full text-left">
				{translations["OrderSummaryMainTitle"]}
			</p>

			<div className="flex w-full flex-col gap-7 p-7 border-ipxon-gradient-black-lighter border bg-zinc-900 rounded-lg">
				{/* Plan details */}
				<PlanDetails
					selectedPlan={selectedPlan}
					selectedLocation={selectedLocation}
					planPrice={planPrice}
					totalPrice={totalPrice}
				/>

				{/* Current config summary */}
				<ConfigurationsSummary values={values} paymentCycle={paymentCycle} />

				{/* Price summary */}
				<PriceSummary
					planPrice={planPriceLoading ? <BeatLoader color="#d1d5db" size={6} /> : planPrice}
					addonPrice={addonPriceLoading ? <BeatLoader color="#d1d5db" size={6} /> : addonPrice}
					totalPrice={totalPriceLoading ? <BeatLoader color="#d1d5db" size={6} /> : totalPrice}
					paymentCycle={paymentCycle}
					selectedPlan={selectedPlan}
				/>

				<div className="flex flex-col gap-4">
					<motion.div>
						<PrimaryButton
							type="button"
							onClick={handleButtonClick}
							disabled={isSubmitting || hasTabErrors}
							className={`!w-full !rounded-md h-[3rem] ${
								hasTabErrors
									? "opacity-50 cursor-not-allowed !transition-none !animate-none hover:!bg-transparent focus:!bg-transparent !bg-pos-100"
									: "opacity-100 cursor-pointer"
							}`}
						>
							{isSubmittingT ? (
								<BeatLoader color="#d1d5db" size={8} />
							) : currentTab === 0 ? (
								translations["OrderSummaryProceedToPayment"]
							) : (
								translations["OrderSummaryConfirmAndCheckout"]
							)}
						</PrimaryButton>
					</motion.div>

					<div className="h-[18px] justify-center inline-flex">
						<button
							type="button" // IMPORTANT: NOT A SUBMIT BUTTON
							onClick={() => (currentTab === 0 ? navigate("/") : setCurrentTab(0))}
							className="text-zinc-400 text-xs"
						>
							{currentTab === 0
								? translations["OrderSummaryBackHome"]
								: translations["OrderSummaryBackToConfiguration"]}
						</button>
					</div>

					<p className="w-full text-center text-xs text-neutral-50">
						{translations["OrderSummaryDisclaimerPartOne"]}{" "}
						<Link to={legalUrl || "#"} className="hover:underline hover:underline-offset-8">
							<strong>{translations["OrderSummaryDisclaimerPartTwo"]}</strong>
						</Link>{" "}
						{translations["OrderSummaryDisclaimerPartThree"]}
					</p>
				</div>
			</div>

			{/* Success modal: only shows if orderCreated === true */}
			{/* {orderCreated && (
				<CheckoutSuccessModal
					isOpen={orderCreated}
					onClose={() => setOrderCreated(false)}
					orderNumber={orderNumber}
					totalPrice={totalPrice}
					pageContext={pageContext}
				/>
			)} */}
		</div>
	);
};

export default OrderSummary;
